/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState } from 'react'
import GetAppIcon from '@material-ui/icons/GetApp'
import { CircularProgress, IconButton, Tabs, Tab } from '@material-ui/core'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { DONUT_CHART_MIN_VALUE } from 'consts/charts'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { DonutChartWithStats } from '../../components/DonutChartWithStats'
import { getUserTestStats, UserTestParams, UserTestResponse, TestCompletionData } from './api'
import CustomTooltip from '../../components/CustomTooltip'
import {
  titleStyle,
  tabsStyle,
  tabStyle,
  explanationStyle,
  errorTextStyle,
  emptyTextStyle,
} from './styles'

interface UserTestProps {
  period: { month: number; year: number }
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const UserTest: React.FC<UserTestProps> = ({ period, trigger, onLoaded }) => {
  const [tabValue, setTabValue] = useState(0) // 0 = Por período, 1 = Por usuarios activos
  const [data, setData] = useState<TestCompletionData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onlyActive = tabValue === 1

  const [statsCall, statsLoading] = useApiCall<UserTestParams, UserTestResponse>(getUserTestStats)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'UserTest' })

  const fetchData = async () => {
    try {
      setError(null)

      const startDate = moment()
        .year(period.year)
        .month(period.month)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')

      const response = await statsCall({
        startDate,
        endDate,
        onlyActive,
      })

      if (response?.data) {
        setData(response.data)
      }
    } catch (apiError) {
      console.error('Error fetching user test data:', apiError)
      setError('Error al cargar datos del test')
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  const formatDonutData = () => {
    if (!data) return []

    const total = data.completed + data.partialProgress + data.neverOpened

    // Si no hay datos, mostrar barras grises con valores 0
    if (total === 0) {
      return [
        {
          name: 'Completó',
          value: DONUT_CHART_MIN_VALUE,
          color: 'gray',
          quantity: 0,
        },
        {
          name: 'Avance parcial',
          value: DONUT_CHART_MIN_VALUE,
          color: 'gray',
          quantity: 0,
        },
        {
          name: 'Desestimado',
          value: DONUT_CHART_MIN_VALUE,
          color: 'gray',
          quantity: 0,
        },
      ]
    }

    return [
      {
        name: 'Completó',
        value: Math.round((data.completed / total) * 100),
        color: 'teal',
        quantity: data.completed,
      },
      {
        name: 'Avance parcial',
        value: Math.round((data.partialProgress / total) * 100),
        color: 'green',
        quantity: data.partialProgress,
      },
      {
        name: 'Desestimado',
        value: Math.round((data.neverOpened / total) * 100),
        color: 'sky',
        quantity: data.neverOpened,
      },
    ]
  }

  const downloadUserTestCSV = () => {
    if (!data) return
    const rows: string[][] = []
    rows.push(['Métrica', 'Valor'])
    rows.push(['Completó', data.completed.toString()])
    rows.push(['Avance parcial', data.partialProgress.toString()])
    rows.push(['Desestimado', data.neverOpened.toString()])
    rows.push(['Total usuarios', data.totalUsers.toString()])
    const responseRate =
      data.totalUsers > 0 ? ((data.completed / data.totalUsers) * 100).toFixed(2) : '0.00'
    rows.push(['Tasa de respuesta (%)', responseRate])
    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'test_usuarios.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, tabValue])

  const isLoading = statsLoading
  const donutData = formatDonutData()

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <CircularProgress size={40} />
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-8">
          <div style={errorTextStyle}>{error}</div>
        </div>
      )
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center py-8">
          <div style={emptyTextStyle}>No hay datos disponibles para mostrar en este momento.</div>
        </div>
      )
    }

    return (
      <div className="flex flex-row items-center justify-center">
        <DonutChartWithStats title="" data={donutData} customTooltip={CustomTooltip} />
        {/* <MetricCard
          title="Usuarios que respondieron el test:"
          value={responseRate}
          isPercent
          className="bg-white shadow-lg p-6 rounded-lg"
        /> */}
      </div>
    )
  }

  const handleTabChange = (_event: React.ChangeEvent<Record<string, never>>, newValue: number) => {
    setTabValue(newValue)
  }

  const getExplanation = () => {
    if (tabValue === 0) {
      return 'Muestra las respuestas al test realizadas durante el período seleccionado, considerando a todos los usuarios.'
    }
    return 'Muestra la interacción con el test de usuarios que tuvieron al menos una visita en el período seleccionado, independientemente de cuándo respondieron el test.'
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-2">
        <span style={titleStyle}>Conversión - Respuesta test</span>
        <IconButton
          disabled={isLoading}
          className="ml-2"
          aria-label="download"
          onClick={downloadUserTestCSV}
        >
          <GetAppIcon />
        </IconButton>
      </div>

      <div className="mb-4">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          style={tabsStyle}
        >
          <Tab label="Por período" style={tabStyle} />
          <Tab label="Por usuarios activos" style={tabStyle} />
        </Tabs>
      </div>

      {renderContent()}

      <div className="mt-4" style={explanationStyle}>
        {getExplanation()}
      </div>
    </div>
  )
}

export default UserTest
